<template>
  <section class="support-dashboard">
    <div class="support-dashboard__metrics" aria-label="Support queue summary">
      <button v-for="metric in metrics" :key="metric.key" type="button" :class="`support-dashboard__metric--${metric.tone}`" @click="openMetric(metric)">
        <span>{{ metric.label }}</span>
        <strong>{{ summary.counts[metric.key] || 0 }}</strong>
      </button>
    </div>

    <app-card design="new" variant="table" header-density="compact" title="Needs attention" class="support-dashboard__attention">
      <div v-if="loading" role="status" aria-label="Loading support dashboard" aria-busy="true">
        <app-data-table design="new" density="dense" :columns="columns" :rows="[]" min-width="1200px" :skeleton-rows="6" loading />
      </div>
      <AppErrorState v-else-if="error" :message="error" @retry="load" />
      <app-no-results v-else-if="!attention.length" design="new" small title="Queue is clear" text="" />
      <support-ticket-table v-else :tickets="attention" :columns="columns" min-width="1200px" :row-class="rowClass">
        <template #cell-ticket="{ ticket }">
          <button class="support-dashboard__ticket" type="button" @click="openTicket(ticket)">
            <strong>{{ ticket.public_number }}</strong>
            <AppTableText :text="ticket.subject" />
          </button>
        </template>
        <template #cell-description="{ ticket }">
          <AppTableText :text="ticket.description_preview" />
        </template>
        <template #cell-classification="{ ticket }">
          <span class="support-dashboard__classification">
            <app-status-badge v-if="ticket.system" design="new" compact :label="ticket.system.name" :color="ticket.system.color" />
            <AppTableText :text="classificationTail(ticket)" fallback="" />
          </span>
        </template>
        <template #cell-tags="{ ticket }"><ticket-table-badges :items="ticket.tags" keyboard-tooltips /></template>
        <template #cell-responsibility="{ ticket }"><ticket-table-badges :items="ticket.responsible_labels" keyboard-tooltips /></template>
        <template #cell-requester="{ ticket }"><ticket-table-person :user="ticket.requester" empty-text="Unknown requester" /></template>
        <template #cell-status="{ ticket }">
          <span v-tooltip="{ ...attentionTooltip(ticket), content: attentionReasons(ticket).join(' · ') || null }" :tabindex="attentionReasons(ticket).length ? 0 : null" class="support-dashboard__status ui-tooltip-target">
            <app-status-badge design="new" compact :label="statusLabel(ticket.status)" :tone="statusTone(ticket.status)" />
            <i v-if="ticket.is_unread" aria-label="Unread"></i>
          </span>
        </template>
        <template #cell-assignee="{ ticket }"><ticket-table-person :user="ticket.assignee" compact /></template>
        <template #cell-age="{ ticket }">
          <strong v-if="ticket.status === 'new'" :class="`support-dashboard__age--${newAgeTone(ticket)}`">{{ statusAge(ticket) }}</strong>
          <span v-else>{{ relativeTime(ticket.last_activity_at) }}</span>
        </template>
      </support-ticket-table>
      <footer v-if="hasMore && !loading && !error" class="support-dashboard__more">
        <app-button design="new" size="small" theme="dark" :disabled="loadingMore" @click.native="loadMore">
          {{ loadingMore ? 'Loading…' : 'Show more' }}
        </app-button>
      </footer>
    </app-card>
  </section>
</template>

<script>
import { getSupportTicketAttention, getSupportTicketDashboard } from '@/http'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import SupportTicketTable from './components/SupportTicketTable'
import TicketTableBadges from './components/TicketTableBadges'
import TicketTablePerson from './components/TicketTablePerson'
import { apiError, normalizePage, relativeTime, statusLabel, statusTone } from './ticketState'

export default {
  name: 'SupportTicketDashboard',
  components: { AppErrorState, AppTableText, SupportTicketTable, TicketTableBadges, TicketTablePerson },
  data() {
    return {
      loading: true,
      loadingMore: false,
      error: '',
      summary: { counts: {} },
      attention: [],
      attentionPage: 1,
      hasMore: false,
      columns: [
        { key: 'ticket', label: 'Ticket', width: '210px' },
        { key: 'description', label: 'Description', width: '220px' },
        { key: 'classification', label: 'Classification', width: '160px' },
        { key: 'tags', label: 'Tags', width: '120px' },
        { key: 'responsibility', label: 'Responsibility', width: '140px' },
        { key: 'requester', label: 'Requester', width: '150px' },
        { key: 'status', label: 'Status', width: '110px' },
        { key: 'assignee', label: 'Assignee', width: '150px' },
        { key: 'age', label: 'Age', width: '80px' }
      ],
      metrics: [
        { key: 'new', label: 'New', query: { status: 'new' }, tone: 'blue' },
        { key: 'unread', label: 'Unread', query: { unread: 'true' }, tone: 'violet' },
        { key: 'unassigned', label: 'Unassigned', query: { unassigned: 'true' }, tone: 'amber' },
        { key: 'waiting', label: 'Waiting', query: { focus: 'waiting' }, tone: 'red' }
      ]
    }
  },
  created() {
    this.load()
    this.$root.$on('support-ticket-refresh', this.load)
  },
  beforeDestroy() {
    this.$root.$off('support-ticket-refresh', this.load)
  },
  methods: {
    relativeTime,
    statusLabel,
    statusTone,
    load(showLoading = true) {
      if (showLoading) this.loading = true
      this.error = ''
      this.attentionPage = 1
      return Promise.all([
        getSupportTicketDashboard(),
        getSupportTicketAttention({ page: 1, page_size: 40 })
      ]).then(([summary, attention]) => {
        this.summary = summary.data
        this.attention = normalizePage(attention.data)
        this.hasMore = Boolean(attention.data.next)
      }).catch(error => { this.error = apiError(error, 'Unable to load the support dashboard.') })
        .finally(() => { this.loading = false })
    },
    loadMore() {
      if (!this.hasMore || this.loadingMore) return
      this.loadingMore = true
      getSupportTicketAttention({ page: this.attentionPage + 1, page_size: 40 }).then(response => {
        const known = new Set(this.attention.map(ticket => ticket.id))
        this.attention = [...this.attention, ...normalizePage(response.data).filter(ticket => !known.has(ticket.id))]
        this.attentionPage += 1
        this.hasMore = Boolean(response.data.next)
      }).catch(error => { this.error = apiError(error) })
        .finally(() => { this.loadingMore = false })
    },
    openTicket(ticket) {
      this.$router.push({ name: this.$route.name, query: { ...this.$route.query, ticket: ticket.id } }).catch(() => {})
    },
    openMetric(metric) { this.$router.push({ name: 'support-ticket-tasks', query: metric.query }) },
    textTooltip(content) { return { content: content || '—', placement: 'top', offset: 8, classes: 'tooltip--new' } },
    classificationTail(ticket) { return [ticket.direction?.name, ticket.module?.name].filter(Boolean).join(' / ') || '' },
    classificationLabel(ticket) { return [ticket.system?.name, ticket.direction?.name, ticket.module?.name].filter(Boolean).join(' / ') || 'No classification' },
    attentionReasons(ticket) {
      return [ticket.is_unread ? 'Unread' : '', !ticket.assignee ? 'Unassigned' : ''].filter(Boolean)
    },
    attentionTooltip(ticket) { return this.textTooltip(this.attentionReasons(ticket).join(' · ') || statusLabel(ticket.status)) },
    ageMinutes(ticket) { return Math.max(0, Math.floor((Date.now() - new Date(ticket.status_changed_at).getTime()) / 60000)) },
    newAgeTone(ticket) {
      const minutes = this.ageMinutes(ticket)
      if (minutes < 10) return 'green'
      if (minutes < 30) return 'yellow'
      if (minutes < 60) return 'orange'
      return 'red'
    },
    statusAge(ticket) {
      const minutes = this.ageMinutes(ticket)
      if (minutes < 1) return '<1m'
      if (minutes < 60) return `${minutes}m`
      const hours = Math.floor(minutes / 60)
      const rest = minutes % 60
      return `${hours}h${rest ? ` ${rest}m` : ''}`
    },
    rowClass(ticket) { return ticket.is_unread ? 'is-unread' : '' }
  }
}
</script>

<style lang="scss" scoped>
.support-dashboard { display: grid; gap: $ui-space-20; }
.support-dashboard__metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: $ui-space-16; }
.support-dashboard__metrics button { display: grid; gap: $ui-space-4; padding: $ui-space-20; border: 1px solid $ui-line; border-radius: $ui-card-radius; background: $ui-surface; box-shadow: $ui-card-shadow; color: $ui-text; cursor: pointer; text-align: left; }
.support-dashboard__metrics button::before { width: 38px; height: 4px; margin-bottom: $ui-space-8; border-radius: 999px; background: var(--metric-accent); content: ''; }
.support-dashboard__metric--blue { --metric-accent: #2563eb; background: linear-gradient(145deg, rgba(37, 99, 235, .09), $ui-surface 62%) !important; }
.support-dashboard__metric--violet { --metric-accent: #7c3aed; background: linear-gradient(145deg, rgba(124, 58, 237, .09), $ui-surface 62%) !important; }
.support-dashboard__metric--amber { --metric-accent: #d97706; background: linear-gradient(145deg, rgba(217, 119, 6, .1), $ui-surface 62%) !important; }
.support-dashboard__metric--red { --metric-accent: #dc2626; background: linear-gradient(145deg, rgba(220, 38, 38, .08), $ui-surface 62%) !important; }
.support-dashboard__metrics button:hover,
.support-dashboard__metrics button:focus-visible { border-color: $ui-blue; outline: 0; }
.support-dashboard__metrics span { color: $ui-muted; font-size: $ui-font-size-metadata; font-weight: 600; text-transform: uppercase; }
.support-dashboard__metrics strong { color: $ui-ink; font: 600 32px $ui-font-heading; }
.support-dashboard__attention { overflow: hidden; }
.support-dashboard__ticket { display: flex; width: 100%; min-width: 0; align-items: center; gap: $ui-space-8; padding: 0; border: 0; background: transparent; color: $ui-text; cursor: pointer; font: inherit; text-align: left; }
.support-dashboard__ticket strong { flex: 0 0 auto; color: $ui-blue; }
.support-dashboard__ticket span,
.support-dashboard__ellipsis { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.support-dashboard__ticket span { color: $ui-ink; font-weight: 600; }
.support-dashboard__classification { display: flex; min-width: 0; align-items: center; gap: 6px; overflow: hidden; }
.support-dashboard__classification > span:last-child { overflow: hidden; color: $ui-muted; text-overflow: ellipsis; }
.support-dashboard__status { display: flex; align-items: center; gap: 6px; }
.support-dashboard__status i { width: 7px; height: 7px; border-radius: 50%; background: $ui-blue; }
.support-dashboard__age--green { color: #15803d; }
.support-dashboard__age--yellow { color: #a16207; }
.support-dashboard__age--orange { color: #c2410c; }
.support-dashboard__age--red { color: #b91c1c; }
.support-dashboard__more { display: flex; justify-content: center; padding: $ui-space-12; border-top: 1px solid $ui-line; }
.support-dashboard__error { color: $ui-red; }
@media (max-width: 1100px) { .support-dashboard__metrics { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 680px) { .support-dashboard__metrics { grid-template-columns: 1fr; } }
</style>
